// Phase 19 · Community V2 · Attachment picker (Wrapper mode)
// Lets user attach a marketplace asset or mini-landing to a DM or channel message.
import React, { useEffect, useState } from "react";
import { X, ShoppingBag, LayoutGrid, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AttachmentPicker({ onClose, onPick }) {
    const [tab, setTab] = useState("marketplace");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                if (tab === "marketplace") {
                    const params = new URLSearchParams({ limit: "18" });
                    if (q) params.set("search", q);
                    const r = await fetch(`${API}/api/marketplace?${params}`, { credentials: "include" });
                    if (r.ok) {
                        const d = await r.json();
                        setItems((d.items || []).map((a) => ({
                            kind: "asset_marketplace",
                            ref: a.slug || a.id,
                            title: a.title,
                            thumb: a.thumbnail_url,
                        })));
                    } else setItems([]);
                } else {
                    const params = new URLSearchParams({ limit: "18" });
                    if (q) params.set("search", q);
                    const r = await fetch(`${API}/api/mini-landings/mine?${params}`, { credentials: "include" });
                    if (r.ok) {
                        const d = await r.json();
                        setItems((d.items || []).map((m) => ({
                            kind: "mini_landing",
                            ref: m.slug || m.id,
                            title: m.title || m.slug,
                            thumb: m.og_image,
                        })));
                    } else setItems([]);
                }
            } catch { setItems([]); }
            setLoading(false);
        };
        load();
    }, [tab, q]);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            data-testid="attachment-picker"
        >
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-2xl overflow-hidden max-h-[85vh] flex flex-col">
                <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-neutral-100">Share something</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-200"
                        data-testid="attachment-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="px-4 pt-3 flex items-center gap-2 text-xs">
                    <button
                        type="button"
                        onClick={() => setTab("marketplace")}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border ${tab === "marketplace" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400"}`}
                        data-testid="attachment-tab-marketplace"
                    >
                        <ShoppingBag size={13} /> Marketplace
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("mini_landing")}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border ${tab === "mini_landing" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400"}`}
                        data-testid="attachment-tab-mini-landing"
                    >
                        <LayoutGrid size={13} /> Mini-landings
                    </button>
                </div>
                <div className="px-4 pt-3 pb-2">
                    <div className="relative">
                        <Search size={13} className="absolute left-3 top-2.5 text-neutral-500" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search…"
                            className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg pl-9 pr-3 py-1.5 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="attachment-search"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {loading && <p className="text-xs text-neutral-500 col-span-full text-center py-4">Loading…</p>}
                    {!loading && !items.length && (
                        <p className="text-xs text-neutral-500 col-span-full text-center py-6">
                            Nothing to share yet
                        </p>
                    )}
                    {items.map((it) => (
                        <button
                            key={`${it.kind}-${it.ref}`}
                            type="button"
                            onClick={() => { onPick?.(it); onClose?.(); }}
                            className="flex items-center gap-3 p-2 rounded-lg border border-[#1A1A1A] hover:border-[#FFFFFF]/50 text-left transition-colors"
                            data-testid={`attach-pick-${it.kind}-${it.ref}`}
                        >
                            {it.thumb ? (
                                <img src={it.thumb} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                            ) : (
                                <div className="w-12 h-12 rounded bg-[#111111] flex items-center justify-center flex-shrink-0">
                                    {it.kind === "asset_marketplace"
                                        ? <ShoppingBag size={18} className="text-[#CCCCCC]" />
                                        : <LayoutGrid size={18} className="text-[#FFFFFF]" />}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-neutral-100 truncate">
                                    {it.title}
                                </p>
                                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                                    {it.kind === "asset_marketplace" ? "Marketplace" : "Mini-landing"}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
